import Badge from '../../../components/ui/Badge';
import Input from '../../../components/ui/Input';
import { MONO_CLASS } from '../../../components/ui/Mono';
import Select from '../../../components/ui/Select';
import {
    PRODUCT_FORMAT_LABELS,
    PRODUCT_SKU_MAX,
    PRODUCT_TITLE_MAX,
    PRODUCT_UNIT_MAX,
    PRODUCT_UNIT_OPTIONS,
} from '../../../constants/productCatalog';
import { BlingSuggestion, FieldCell, FieldInputProps, FormSection, ReadOnlyField } from '../editorParts';
import { FieldDomId, UpperCollapse } from '../productForm';

const UNIT_DATALIST_ID = 'produto-unidades';

/**
 * Dados gerais: nome, SKU (texto só leitura na edição — é a chave), preço de
 * venda, unidade, marca da lista fechada e formato (Kit só pelo Bling).
 */
const GeneralSection = ({
    ctx,
    mode,
    product,
    brands,
    brandsLoading,
    brandsError,
    canCreateBrand,
    onNewBrand,
    skuChecking,
}) => {
    const { form, setField, readOnly, suggestions, capturedAt } = ctx;
    const titleLength = UpperCollapse(form.title).length;
    const brandInList = !form.brand || brands.some((item) => item.brand === form.brand);

    const brandAside =
        canCreateBrand && !readOnly ? (
            <button
                type="button"
                onClick={onNewBrand}
                className="font-medium text-link-600 hover:underline dark:text-link-400"
            >
                + Nova marca
            </button>
        ) : null;

    return (
        <FormSection id="geral" title="Dados gerais" description="Identificação, preço de venda e marca do produto.">
            <FieldCell span={6}>
                <Input
                    label={
                        <>
                            Nome <span className="text-danger-500">*</span>
                        </>
                    }
                    labelAside={
                        <span className={titleLength > PRODUCT_TITLE_MAX ? 'text-danger-600' : undefined}>
                            <span className={MONO_CLASS}>
                                {titleLength}/{PRODUCT_TITLE_MAX}
                            </span>
                        </span>
                    }
                    placeholder="Nome do produto"
                    maxLength={PRODUCT_TITLE_MAX + 20}
                    autoComplete="off"
                    className="uppercase"
                    {...FieldInputProps(ctx, 'title')}
                />
            </FieldCell>

            <FieldCell span={2}>
                {mode === 'new' ? (
                    <Input
                        label={
                            <>
                                Código (SKU) <span className="text-danger-500">*</span>
                            </>
                        }
                        placeholder="ex.: ABC-12345"
                        maxLength={PRODUCT_SKU_MAX + 10}
                        autoComplete="off"
                        spellCheck={false}
                        className={`${MONO_CLASS} uppercase`}
                        {...FieldInputProps(ctx, 'sku', {
                            hint: skuChecking
                                ? 'Conferindo se o SKU está livre…'
                                : 'Chave do produto; não muda depois de salvar',
                        })}
                    />
                ) : (
                    <ReadOnlyField
                        id={FieldDomId('sku')}
                        label="Código (SKU)"
                        mono
                        value={product?.sku_display || product?.sku || '—'}
                        warning={ctx.warnings.sku}
                        hint="Chave do produto; não muda depois de salvar"
                    />
                )}
            </FieldCell>

            <FieldCell span={2}>
                <Input
                    label="Preço de venda"
                    prefix="R$"
                    inputMode="decimal"
                    placeholder="0,00"
                    autoComplete="off"
                    className={`${MONO_CLASS} text-right`}
                    {...FieldInputProps(ctx, 'sale_price')}
                />
                <BlingSuggestion
                    suggestion={suggestions.sale_price}
                    capturedAt={capturedAt}
                    disabled={readOnly}
                    onUse={(value) => setField('sale_price', value)}
                />
            </FieldCell>

            <FieldCell span={2}>
                <Input
                    label="Unidade"
                    list={UNIT_DATALIST_ID}
                    maxLength={PRODUCT_UNIT_MAX}
                    placeholder="UN"
                    autoComplete="off"
                    className="uppercase"
                    {...FieldInputProps(ctx, 'unit')}
                />
                <datalist id={UNIT_DATALIST_ID}>
                    {PRODUCT_UNIT_OPTIONS.map((unit) => (
                        <option key={unit} value={unit} />
                    ))}
                </datalist>
            </FieldCell>

            <FieldCell span={3}>
                <Select
                    label="Marca"
                    labelAside={brandAside}
                    {...FieldInputProps(ctx, 'brand', {
                        hint: brandsError ? `Lista de marcas indisponível: ${brandsError}` : null,
                    })}
                    disabled={readOnly || brandsLoading}
                >
                    <option value="">{brandsLoading ? 'Carregando marcas…' : 'Sem marca'}</option>
                    {/* Valor antigo fora da lista continua selecionável (senão o select
                        mostraria outra opção); o rótulo só acusa com a lista carregada. */}
                    {!brandInList ? (
                        <option value={form.brand}>
                            {brands.length > 0 ? `${form.brand} (fora da lista)` : form.brand}
                        </option>
                    ) : null}
                    {brands.map((item) => (
                        <option key={item.brand} value={item.brand}>
                            {item.brand}
                        </option>
                    ))}
                </Select>
                <BlingSuggestion
                    suggestion={suggestions.brand}
                    capturedAt={capturedAt}
                    disabled={readOnly}
                    onUse={(value) => setField('brand', value)}
                />
            </FieldCell>

            <FieldCell span={3}>
                <ReadOnlyField
                    id={FieldDomId('format')}
                    label="Formato"
                    value={
                        <Badge variant={form.format === 'KIT' ? 'info' : 'default'}>
                            {PRODUCT_FORMAT_LABELS[form.format] || PRODUCT_FORMAT_LABELS.SIMPLE}
                        </Badge>
                    }
                    hint={
                        mode === 'new'
                            ? 'Kits continuam sendo cadastrados no Bling'
                            : form.format === 'KIT'
                              ? 'A composição do kit é mantida no Bling'
                              : 'O formato não muda aqui'
                    }
                />
            </FieldCell>
        </FormSection>
    );
};

export default GeneralSection;
